import * as z from "zod";

const ACCEPTED_IMAGE_TYPES = [
  "image/jpg",
  "image/png",
  "image/jpeg",
  undefined,
  null,
];

export const TenantFormRule = z.object({
  owner: z.string().optional(),
  businessName: z.string().optional(),
  businessType: z.string().optional(),
  description: z.string().optional(),
  avatar: z.string().optional(),
  email: z.string().optional(),
  mobileNumber: z.string().optional(),
  phoneNumber: z.string().optional(),
  website: z.string().optional(),
  membershipDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
  rewardsType: z.string().optional(),
  slug: z.string().optional(),
  postRegistrationLink: z.string().optional(),
  enablePublicBookingTimeSelection: z.boolean().optional(),
  dailyBookingLimit: z.number().optional(),
  bookingLimitMessage: z.string().optional(),
  showAppointmentDetails: z.boolean().optional(),
  showServicesInPublicBooking: z.boolean().optional(),
  defaultDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
});
export const RoleFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
});
export const PersonFormRule = z.object({
  tenant: z.string().optional(),
  role: z.string().optional(),
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  username: z.string().optional(),
  email: z.string().optional(),
  password: z.string().optional(),
  isVerified: z.boolean().optional(),
  avatar: z.string().optional(),
  activationCode: z.string().optional(),
  gender: z.string().optional(),
  birthday: z.coerce.date().optional().nullish().or(z.string().max(0)),
  isBlocked: z.boolean().optional(),
  mobileNumber: z.string().optional(),
  fatherName: z.string().optional(),
  motherName: z.string().optional(),
  emergencyContact_1Number: z.string().optional(),
  emergencyContact_1Name: z.string().optional(),
  emergencyContact_1Relationship: z.string().optional(),
  emergencyContact_2Number: z.string().optional(),
  emergencyContact_2Name: z.string().optional(),
  emergencyContact_2Relationship: z.string().optional(),
  sss: z.string().optional(),
  philhealth: z.string().optional(),
  pagibig: z.string().optional(),
  tin: z.string().optional(),
  medicalCondition: z.string().optional(),
  bloodType: z.string().optional(),
  elementary: z.string().optional(),
  highSchool: z.string().optional(),
  college: z.string().optional(),
  postCollege: z.string().optional(),
  undergraduate: z.string().optional(),
  curriculumVitae: z.string().optional(),
  supportingDoc: z.string().optional(),
  intentLetter: z.string().optional(),
  applicationLetter: z.string().optional(),
  photo: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported.",
    ),
});
export const PatientFormRule = z.object({
  tenant: z.string(),
  firstName: z.string().min(1, "This field is required"),
  middleName: z.string().optional(),
  lastName: z.string().min(1, "This field is required"),
  birthday: z.string().optional(),
  gender: z.string().min(1, "This field is required"),
  address: z.string().optional(),
  email: z.string().min(1, "This field is required"),
  mobileNumber: z.string().optional(),
  photo: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported.",
    ),
  isTakingMedication: z.boolean().optional(),
  medicationDetails: z.string().optional(),
  hasAllergies: z.boolean().optional(),
  allergyDetails: z.string().optional(),
  hasMedicalConditions: z.boolean().optional(),
  medicalConditionDetails: z.string().optional(),
  wasRecentlyUnwell: z.boolean().optional(),
  unwellDetails: z.string().optional(),
  otherMedicalConditions: z.string().optional(),
});
export const UtilServiceFormRule = z.object({
  tenant: z.string().optional(),
  category: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  amount: z.number().min(1, "Please enter amount"),
});

export const UtilServicePublicFormRule = z.object({
  tenant: z.string().optional(),
});

export const ContractFormRule = z.object({
  tenant: z.string().optional(),
  patient: z.string().min(1, "Patient is required"),
  service: z.string().optional(),
  signature: z.string().optional(),
  amount: z.number().min(1, "Please enter amount"),
  file: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported.",
    ),
  session: z.number().min(1, "Please enter number of sessions"),
  patientName: z.string().optional(),
  serviceName: z.string().optional(),
  personName: z.string().optional(),
  person: z.string().optional(),
  waiver: z.string().min(1, "Waiver is required"),
  waiverName: z.string().optional(),
  waiverChecks: z.array(z.boolean()).optional(),
});
export const BillingFormRule = z.object({
  contract: z.string().optional(),
  session: z.number().optional(),
});
export const ProviderTypeFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  tenant: z.string().optional(),
});

export const PaymentFormRule = z.object({
  billing: z.string().optional(),
  amountPaid: z.number().optional(),
  status: z.string().optional(),
  avatar: z.string().optional(),
  provider: z.string().optional(),
  attachment: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported.",
    ),
  contract: z.string().optional(),
});
export const VisitFormRule = z.object({
  patient: z.string().optional(),
  contract: z.string().optional(),
  billing: z.string().optional(),
  date: z.string().optional(),
  time: z.string().optional(),
  description: z.string().optional(),
  prescription: z.string().optional(),
  person: z.string().optional(),
  avatar: z.string().optional(),
  photos: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported.",
    ),
  patientName: z.string().optional(),
  doctorName: z.string().optional(),
  attachment: z.any().optional(),
});
export const WaiverFormRule = z.object({
  tenant: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  subtitle: z.string().optional(),
  fontSize: z.number().optional(),
});
export const RegistrationFormRule = z.object({
  tenant: z.string().optional(),
  firstName: z.string().min(1, "This field is required"),
  middleName: z.string().optional(),
  lastName: z.string().min(1, "This field is required"),
  birthday: z.string().optional(),
  gender: z.string().optional(),
  address: z.string().optional(),
  mobileNumber: z.string().optional(),
  avatar: z.string().optional(),
  email: z.string().min(1, "This field is required"),
  isTakingMedication: z.boolean().optional(),
  medicationDetails: z.string().optional(),
  hasAllergies: z.boolean().optional(),
  allergyDetails: z.string().optional(),
  hasMedicalConditions: z.boolean().optional(),
  medicalConditonDetails: z.string().optional(),
  wasRecentlyUnwell: z.boolean().optional(),
  unwellDetails: z.string().optional(),
  otherMedicalConditions: z.string().optional(),
  service: z.string().optional(),
  serviceType: z.string().optional(),
  date: z.string().optional(),
  time: z.string().optional(),
  otherRemarks: z.string().optional(),
  consentAgreement: z.boolean().optional(),
  status: z.string().optional(),
});

export const ApproveRegistrationFormRule = z.object({
  code: z.string(),
  status: z.string().optional(),
});
export const UtilCategoryFormRule = z.object({
  tenant: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
});
// RULE ADD
export const LoginRule = z.object({
  username: z.string().min(1, "Email/Username is required"),
  password: z.string().min(1, { message: "Password is required" }),
});

export type LoginFormSchema = z.infer<typeof LoginRule>;
export type TenantFormSchema = z.infer<typeof TenantFormRule>;
export type RoleFormSchema = z.infer<typeof RoleFormRule>;
export type PersonFormSchema = z.infer<typeof PersonFormRule>;
export type PatientFormSchema = z.infer<typeof PatientFormRule>;
export type UtilServiceFormSchema = z.infer<typeof UtilServiceFormRule>;
export type ContractFormSchema = z.infer<typeof ContractFormRule>;
export type BillingFormSchema = z.infer<typeof BillingFormRule>;
export type ProviderTypeFormSchema = z.infer<typeof ProviderTypeFormRule>;

export type PaymentFormSchema = z.infer<typeof PaymentFormRule>;
export type VisitFormSchema = z.infer<typeof VisitFormRule>;
export type WaiverFormSchema = z.infer<typeof WaiverFormRule>;
export type RegistrationFormSchema = z.infer<typeof RegistrationFormRule>;
export type ApproveRegistrationFormSchema = z.infer<
  typeof ApproveRegistrationFormRule
>;
export type UtilServicePublicFormSchema = z.infer<
  typeof UtilServicePublicFormRule
>;
export type UtilCategoryFormSchema = z.infer<typeof UtilCategoryFormRule>;
// SCHEMA ADD
